import { portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The organization finance overview — plan-014 unit **D6** (design §27.15.1 Finance; FR12-DOC-05; AC12-06).
 *
 * Every balance the person may read, grouped by balance and broken down by workspace, with THREE FIGURES KEPT APART —
 * settled (charged for the month), pending (payments started and not confirmed) and estimated (ended talk the day close
 * has not charged yet) — and the instant and timezone they were calculated in, disclosed in words. Drawn with the
 * Organization renderer's blocks (`op-*`: the `stats` block and the table of the prototype's `funding` and `docs`
 * pages), so it sits in Finance unchanged; mounted on Finance → Documents above the legal details (the prototype has no
 * page of its own for it; a later registration of `/finance/overview` mounts this same component).
 *
 * A delegate of a dedicated balance sees that balance alone (the server decides: `reader = delegated`); the copy says
 * so. Nothing here computes money: the figures are the server's, printed with `formatMoney`.
 */
import { useQuery } from "@tanstack/react-query";
import { money, type CurrencyCode } from "@wetalk/domain";
import type { ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type { FinanceOverviewView } from "../../../api/slice/financial-document.js";
import { formatMoney } from "../../../money.js";
import { Block, BlockNote, StatGrid } from "../../account-admin/page/organization-page.js";

import "./finance-overview.css";

export const FINANCE_OVERVIEW_COPY = {
  get title() { return portalText("finance", "m1235"); },
  get settled() { return portalText("finance", "m0621"); },
  get settledNote() { return portalText("finance", "m1262"); },
  get pending() { return portalText("finance", "m0622"); },
  get pendingNote() { return portalText("finance", "m1263"); },
  get estimated() { return portalText("finance", "m0623"); },
  get estimatedNote() { return portalText("finance", "m1264"); },
  get delegated() { return portalText("finance", "m1236"); },
  get empty() { return portalText("finance", "m1237"); },
  get loading() { return portalText("finance", "m1238"); },
  get definition() { return portalText("finance", "m1239"); },
} as const;

/** The filters the overview takes (the Documents page passes its own). */
export interface FinanceOverviewFilter {
  readonly period: string | null;
  readonly workspaceId: string | null;
  readonly billingAccountId: string | null;
}

export function financeOverviewKey(accountId: string, filter: FinanceOverviewFilter): readonly unknown[] {
  return ["finance-overview", accountId, filter.period ?? "current", filter.workspaceId ?? "all", filter.billingAccountId ?? "all"];
}

const MONTH = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"] as const;

/** `2026-10` → "October 2026". */
export function monthWordOf(month: string): string {
  if (portalLocale() === "el" && /^\d{4}-\d{2}$/.test(month)) return new Intl.DateTimeFormat("el-GR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${month}-01T12:00:00Z`));
  const [year, number] = month.split("-");
  return `${MONTH[Number(number) - 1] ?? ""} ${year ?? ""}`.trim();
}

/** "Calculated at 14:03 on 20 October, Europe/Athens time." — the instant in the zone it was worked out in. */
export function calculatedWordOf(overview: Pick<FinanceOverviewView, "calculatedAt" | "timeZone">): string {
  const at = new Date(overview.calculatedAt);
  const time = new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { timeZone: overview.timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(at);
  const day = new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { timeZone: overview.timeZone, day: "numeric", month: "long" }).format(at);
  return portalText("finance", "m0618", { value1: time, value2: day, value3: overview.timeZone });
}

function amount(minor: number, currency: string | null): string {
  if (currency === null) return "—";
  return formatMoney(money(minor, currency as CurrencyCode));
}

export function FinanceOverviewTable(props: { readonly overview: FinanceOverviewView }): ReactNode {
  usePortalTranslation("finance");
  const { overview } = props;
  return (
    <div className="op-table-wrap">
      <table className="table op-table fo-table">
        <caption className="fo-caption">{portalText("finance", "m0619", { value1: monthWordOf(overview.period.month) })}</caption>
        <thead>
          <tr>
            <th>{portalText("finance", "m0620")}</th>
            <th className="fo-right">{portalText("finance", "m0621")}</th>
            <th className="fo-right">{portalText("finance", "m0622")}</th>
            <th className="fo-right">{portalText("finance", "m0623")}</th>
          </tr>
        </thead>
        <tbody>
          {overview.balance.map((one) => (
            <FinanceBalanceRows key={one.billingAccountId} balance={one} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FinanceBalanceRows(props: { readonly balance: FinanceOverviewView["balance"][number] }): ReactNode {
  usePortalTranslation("finance");
  usePortalTranslation("billing");
  const { balance } = props;
  const scopeWord = balance.scope === "account" ? "shared" : portalText("finance", "m0624");
  return (
    <>
      <tr className="fo-balance">
        <td className="fo-cell">
          <span className="fo-name">{balance.displayName ?? portalText("finance", "m0298")}</span>
          <span className="fo-sub">{balance.lifecycleState === "active" ? scopeWord : `${scopeWord} · ${balance.lifecycleState}`}</span>
        </td>
        <td className="fo-cell fo-right">
          {amount(balance.settledMinor, balance.currency)}
          {/* plan-020 WAPP-BILL (FEE hand-off D): a plan fee is spend for the balance, shown as its own line — it names
              no workspace, so it is in none of the workspace rows below. */}
          {balance.subscriptionFeeMinor === 0 ? null : (
            <span className="fo-sub">{portalText("billing", "overview.plan_fee", { amount: amount(balance.subscriptionFeeMinor, balance.currency) })}</span>
          )}
        </td>
        <td className="fo-cell fo-right">
          {amount(balance.pendingMinor, balance.currency)}
          {balance.pendingCount > 0 ? <span className="fo-sub">{portalText("finance", "m0625", { value1: String(balance.pendingCount) })}</span> : null}
        </td>
        <td className="fo-cell fo-right">{amount(balance.estimatedMinor, balance.currency)}</td>
      </tr>
      {balance.workspace.map((each) => (
        <tr key={`${balance.billingAccountId}-${each.workspaceId}`} className="fo-workspace">
          <td className="fo-cell fo-indent">
            {each.workspaceName}
            {each.fundedSince === null ? <span className="fo-sub">{portalText("finance", "m0626")}</span> : null}
          </td>
          <td className="fo-cell fo-right">{amount(each.settledMinor, balance.currency)}</td>
          <td className="fo-cell fo-right fo-dim">—</td>
          <td className="fo-cell fo-right">{amount(each.estimatedMinor, balance.currency)}</td>
        </tr>
      ))}
    </>
  );
}

/** The overview block, over the person's organization and the page's filters. */
export function FinanceOverviewBlock(props: { readonly accountId: string; readonly filter: FinanceOverviewFilter }): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const overview = useQuery({
    queryKey: financeOverviewKey(props.accountId, props.filter),
    queryFn: () => gateway.readFinanceOverview(props.accountId, props.filter),
  });
  if (overview.isPending) {
    return (
      <Block title={FINANCE_OVERVIEW_COPY.title}>
        <BlockNote>{FINANCE_OVERVIEW_COPY.loading}</BlockNote>
      </Block>
    );
  }
  if (overview.isError) {
    return (
      <Block title={FINANCE_OVERVIEW_COPY.title}>
        <BlockNote>{apiProblemText(overview.error, portalText("finance", "m0627"))}</BlockNote>
      </Block>
    );
  }
  const data = overview.data;
  const period = `${monthWordOf(data.period.month)}${data.period.inProgress ? portalText("finance", "m0628") : ""}`;
  return (
    <Block title={FINANCE_OVERVIEW_COPY.title} dek={`${period}. ${calculatedWordOf(data)}`}>
      <StatGrid
        stat={[
          { label: FINANCE_OVERVIEW_COPY.settled, value: amount(data.total.settledMinor, data.currency), note: FINANCE_OVERVIEW_COPY.settledNote },
          { label: FINANCE_OVERVIEW_COPY.pending, value: amount(data.total.pendingMinor, data.currency), note: FINANCE_OVERVIEW_COPY.pendingNote },
          { label: FINANCE_OVERVIEW_COPY.estimated, value: amount(data.total.estimatedMinor, data.currency), note: FINANCE_OVERVIEW_COPY.estimatedNote },
        ]}
      />
      {data.balance.length === 0 ? <BlockNote>{FINANCE_OVERVIEW_COPY.empty}</BlockNote> : <FinanceOverviewTable overview={data} />}
      <p className="fo-definition">{FINANCE_OVERVIEW_COPY.definition}</p>
      {data.reader === "delegated" ? <p className="fo-definition">{FINANCE_OVERVIEW_COPY.delegated}</p> : null}
    </Block>
  );
}
